import { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridRowClassNameParams,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import PageContainer from "./PageContainer";
import useCountries from "../hooks/useCountries";
import { EUDRBenchmarking, EUDR_DISPLAY_TEXT } from "../constants/eudr";

interface Row {
  id: number;
  country: string;
  ipi_score: string;
  timber_score: string;
  cpi_score: string;
  idat_score: string;
  eudr_benchmarking: string;
  fsc_total: number;
  fsc_v2_total: number;
  slug: string;
}

export default function CompareCountries() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allCountries, searchCountries } = useCountries();
  const [rows, setRows] = useState<Row[]>([]);
  const { state, pathname } = useLocation();

  useEffect(() => {
    searchCountries("", "name", 0, 300);
  }, []);

  const moveCountryToTop = (listOfCountries: any, countrySlug: string) => {
    const matchedFilteredCountries = listOfCountries.filter(
      (country: any) => country.meta.slug == countrySlug
    );
    const filteredCountries = listOfCountries.filter(
      (country: any) => country.meta.slug != countrySlug
    );
    const reOrder = [...matchedFilteredCountries, ...filteredCountries];

    reOrder.map((country: any) => {
      let picked = {
        id: country.id,
        country: country.name,
        ipi_score: country.ipi_score !== null ? country.ipi_score : "",
        timber_score: country.timber_risk_score !== null ? country.timber_risk_score : "",
        cpi_score: country.cpi_score !== null ? country.cpi_score : "",
        idat_score: country.forest_trends_idat_score !== null ? country.forest_trends_idat_score : "",
        eudr_benchmarking: country.eudr_country_benchmarking !== null ? country.eudr_country_benchmarking : "",
        fsc_total: country?.fsc_total_risk,
        fsc_v2_total: country?.fsc_v2_total_risk,
        slug: country.meta.slug,
      };
      setRows((rows) => [...rows, picked]);
    });
  };

  const CustomToolTip = (name: string, tooltipString: string) => {
    return (
      <Tooltip title={tooltipString} placement="top">
        <div className="MuiDataGrid-columnHeaderTitle css-1jbbcbn-MuiDataGrid-columnHeaderTitle">
          {name}
        </div>
      </Tooltip>
    );
  };

  useEffect(() => {
    setRows([]);
    if (allCountries.items) {
      const countrySlug: string = searchParams.get("country") as string;
      moveCountryToTop(allCountries.items, countrySlug);
    }
  }, [allCountries, searchParams]);

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "country",
      headerName: "Country",
      editable: false,
      sortable: true,
      flex: 1.3,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "fsc_total",
      headerName: "FSC v1 NRA Specified Risk Indicators",
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fsc_v2_total",
      headerName: "FSC v2 NRA Non-negligible Risk Indicators",
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ipi_score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Index of Public Integrity (IPI) Score",
          "Standardized measure of six measures for public integrity and transparency. Updated annually.  Source: CorruptionRisk.org."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "timber_score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Timber Sourcing Hub Risk Score",
          "Comprehensive assessment of timber illegality associated with specific countries. Source: Preferred by Nature Timber Sourcing Hub."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "cpi_score",
      headerName: "Corruption Perceptions Index (CPI) Score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Corruption Perceptions Index (CPI) Score",
          "Measure of perceived government corruption as determined by a combination of surveys and independent assessment.  Updated annually. Source: Transparency International"
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "idat_score",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "Forest Trends' IDAT Risk Score",
          "Forest Trends' IDAT (Illegal Deforestation Assessment Tool) score."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "eudr_benchmarking",
      renderHeader: (params: GridColumnHeaderParams) =>
        CustomToolTip(
          "EUDR Country Benchmarking",
          "EUDR Country Benchmarking assessment indicating the risk level for this country under the EU Deforestation Regulation."
        ),
      editable: false,
      sortable: true,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) => {
        if (!params.value) return "";
        return EUDR_DISPLAY_TEXT[params.value as EUDRBenchmarking] || params.value;
      },
    },
  ];

  const handleCellSelect = (params: GridCellParams) => {
    navigate(`/assess-risk/countries/${params.row.slug}`, {
      state: {
        id: params.id,
        name: params.value,
        slug: params.row.slug,
      },
    });
  };

  return (
    <PageContainer style={{ alignItems: "center" }} title={"Compare"}>
      <Box width="90%">
        <Paper elevation={3}>
          <DataGrid
            components={{
              LoadingOverlay: LinearProgress,
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            loading={rows.length <= 0}
            rows={rows}
            columns={columns}
            disableColumnFilter
            getRowClassName={(params: GridRowClassNameParams<any>) => {
              if (params.row.slug === searchParams.get("country")) {
                return "highlighted";
              } else {
                return "";
              }
            }}
            hideFooter
            autoHeight
            headerHeight={80}
            disableColumnMenu={true}
            sx={{
              marginTop: "10px",
              width: "100%",
              "& .MuiDataGrid-columnHeaderTitle": {
                textOverflow: "clip",
                whiteSpace: "break-spaces",
                lineHeight: 1.2,
              },
              "& .highlighted": {
                backgroundColor: theme.palette.wwfGreen.main,
              },
            }}
            onCellClick={(params: GridCellParams) => {
              handleCellSelect(params);
            }}
          />
        </Paper>
      </Box>
    </PageContainer>
  );
}
